import React, { useEffect, useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Button, Grid, Typography } from '@mui/material'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import BatteryDataSelection from './BatteryDataSelection'
import { BatteryExportModule, BatteryJob, BatteryTables, BatteryType, getAllTableColumns, TableName } from './BatteryExportUtils'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import BatteryJobComponent from './BatteryJobComponent'
import { useValidation } from '@e3dc-react/shell/Hooks/useValidation/useValidation'
import { batterExportSchema } from './BatteryExportValidation'

const useStyles = makeStyles()(theme => ({
  exportStartButton: {
    display: 'flex',
    width: '100%',
    marginTop: theme.spacing(2),
    justifyContent: 'center',
  },
}))

export interface BatteryExportModuleProps {
  batteryExportModuleId?: number
  place: number
  onClose: () => void
}

const BatterExportModuleEditor: React.VFC<BatteryExportModuleProps> = ({ batteryExportModuleId, place, onClose }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const authFetch = useAuthFetchWithResult()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const [isValidJob, setIsValidJob] = useState(true)

  const defaultJob: BatteryJob = {
    type: BatteryType.SN,
    id: -1, // serialno
    from: -1,
    to: -1,
    dcb_history: [],
  }

  const defaultTable: BatteryTables = {
    batterydata: {
      columns: ['all'],
      isSelected: true,
    },
    batterydataExLG: {
      columns: ['all'],
      isSelected: true,
    },
    dcbdata: {
      columns: ['all'],
      isSelected: true,
    },
    dcbconfiguration: {
      columns: ['all'],
      isSelected: true,
    },
    batteryconfiguration: {
      columns: ['all'],
      isSelected: true,
    },
  }

  const [batteryExport, setBatteryExport] = useState<BatteryExportModule>({
    jobs: [defaultJob],
    tables: defaultTable,
  })

  const [currPayload, setCurrPayload] = useState<BatteryExportModule>({ ...batteryExport })

  const [validationActive, setValidationActive] = useState(false)
  const validationObject = useValidation(currPayload, batterExportSchema)
  // if validation is inactive the validation object to compare input is empty (input is always valid)
  const validation = validationActive ? validationObject : {}

  /**
   * removes a job from the export job list
   * @param index index of job to remove
   */
  const onRemoveJob = (index: number): void => {
    if (batteryExport) {
      const jobsCopy = [...(batteryExport.jobs as BatteryJob[])]
      jobsCopy.splice(index, 1)
      setBatteryExport({ ...batteryExport, jobs: jobsCopy })
    }
  }

  // update curr payload if battery export tables have changed and bring tables in right format
  useEffect(() => {
    const tables = { ...batteryExport.tables }
    const selectedTables: BatteryTables = Object.entries(tables).reduce((prev, curr) => {
      if (curr[1].isSelected) {
        return { ...prev, [curr[0]]: { columns: curr[1].columns.includes('all') ? getAllTableColumns(curr[0] as TableName) : curr[1].columns } }
      } else {
        return { ...prev }
      }
    }, {})
    setCurrPayload({ ...batteryExport, tables: selectedTables })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batteryExport])

  /**
   * Creates the new battery data export
   */
  const onStartExport = (): void => {
    // 1. validate the input before saving
    if (!validationActive) setValidationActive(true)
    if (Object.keys(validationObject).length > 0 || !isValidJob) return

    // 2. send payload to BE
    authFetch<BatteryExportModule>({
      method: 'POST',
      endpoint: process.env.REACT_APP_CSV_ARCHIVE_API_ENDPOINT,
      path: 'csv-battery-archive/export',
      payload: currPayload,
    })
      .then(() => enqueueSuccessStack('export.job.success'))
      .catch(() => enqueueErrorStack('export.job.error'))

    // 3. close dialog
    onClose()
  }

  return (
    <Dialog
      title={batteryExportModuleId ? t('batteryExportModuleEditor.edit') : t('batteryExportModuleEditor.add')}
      onClose={() => onClose()}
      place={place}
    >
      <Grid container spacing={2} paddingTop={2}>
        {/** Recipient's emails */}
        <Grid item xs={12}>
          <Typography variant={'subtitle2'}>{t('batteryExportModuleEditor.recipient')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('batteryExportModuleEditor.emails')}
            value={batteryExport.emails?.join()}
            onChange={value => setBatteryExport({ ...batteryExport, emails: value.replaceAll(' ', '').split(',') })}
            data-qa={'input-batteryExport-emails'}
            helperText={validation['emails'] ? validation['emails'].message : undefined}
          />
        </Grid>

        {/** Table and Columns to export data from */}
        <Grid item xs={12} marginTop={2}>
          <Typography variant={'subtitle2'}>{t('batteryExportModuleEditor.exportData')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <BatteryDataSelection batteryExport={batteryExport} setBatteryExport={setBatteryExport} validation={validation} />
        </Grid>

        {/** Export job which specefies the relevant battery and it's history */}
        <Grid item xs={12} marginTop={2}>
          <Typography variant={'subtitle2'}>{t('batteryExportModuleEditor.jobs')}</Typography>
        </Grid>
        <Grid item xs={12} marginTop={2}>
          {batteryExport.jobs?.map((job, index) => (
            <BatteryJobComponent
              key={index}
              index={index}
              removeJob={onRemoveJob}
              batteryExport={batteryExport}
              setBatteryExport={setBatteryExport}
              isDisableDeleteJob={batteryExport?.jobs?.length === undefined || batteryExport?.jobs?.length <= 1}
              validationActive={validationActive}
              validation={validation}
              isValidJob={isValidJob}
              setIsValidJob={setIsValidJob}
            />
          ))}
        </Grid>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setBatteryExport({ ...batteryExport, jobs: [...batteryExport.jobs!, defaultJob] })}
        >
          {'Add Job'}
        </Button>
        <div className={classes.exportStartButton}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => onStartExport()}
            disabled={validationActive && (Object.keys(validationObject).length > 0 || !isValidJob)}
          >
            {t('batteryExportModuleEditor.exportStart')}
          </Button>
        </div>
      </Grid>
    </Dialog>
  )
}
export default BatterExportModuleEditor
